import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Upload, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/15 bg-white/[0.03] px-4 py-3.5 text-sm text-[#F4F4F0] placeholder-[#8C8C8C]/70 outline-none transition-colors duration-300 focus:border-[#2FBF8F]";
const labelCls = "mb-2 block text-[11px] font-medium uppercase tracking-[0.22em] text-[#8C8C8C]";

export const QuoteForm = ({ defaultRevetement = "" }) => {
  const [form, setForm] = useState({
    prenom: "", nom: "", telephone: "", email: "", ville: "",
    type_client: "", type_projet: "", surface: "",
    revetement: defaultRevetement, delai: "", description: "",
  });
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      photos.forEach((p) => data.append("photos", p));
      await axios.post(`${API}/devis`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError("Une erreur est survenue. Merci de réessayer ou de nous écrire directement.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-[#047857]/40 bg-[#047857]/10 p-10 text-center"
        data-testid="devis-success"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#047857]">
          <Check size={26} className="text-white" />
        </span>
        <h3 className="mt-6 font-serif text-3xl font-light text-[#F4F4F0]">Merci, votre demande est bien envoyée.</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[#8C8C8C]">
          L'équipe Resineko étudie votre projet et revient vers vous rapidement pour en discuter.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="devis-form" className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="prenom" className={labelCls}>Prénom *</label>
          <input id="prenom" data-testid="devis-prenom" required className={inputCls} value={form.prenom} onChange={set("prenom")} placeholder="Jean" />
        </div>
        <div>
          <label htmlFor="nom" className={labelCls}>Nom *</label>
          <input id="nom" data-testid="devis-nom" required className={inputCls} value={form.nom} onChange={set("nom")} placeholder="Dupont" />
        </div>
        <div>
          <label htmlFor="telephone" className={labelCls}>Téléphone *</label>
          <input id="telephone" data-testid="devis-telephone" required type="tel" className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="06 00 00 00 00" />
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>Email *</label>
          <input id="email" data-testid="devis-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jean.dupont@email.fr" />
        </div>
        <div>
          <label htmlFor="ville" className={labelCls}>Ville</label>
          <input id="ville" data-testid="devis-ville" className={inputCls} value={form.ville} onChange={set("ville")} placeholder="Toulouse" />
        </div>
        <div>
          <label htmlFor="type_client" className={labelCls}>Vous êtes</label>
          <select id="type_client" data-testid="devis-type-client" className={inputCls} value={form.type_client} onChange={set("type_client")}>
            <option value="" className="bg-[#111]">Sélectionner</option>
            <option value="Particulier" className="bg-[#111]">Particulier</option>
            <option value="Professionnel" className="bg-[#111]">Professionnel</option>
            <option value="Industriel" className="bg-[#111]">Industriel</option>
            <option value="Syndic / Collectivité" className="bg-[#111]">Syndic / Collectivité</option>
          </select>
        </div>
        <div>
          <label htmlFor="type_projet" className={labelCls}>Type de projet</label>
          <select id="type_projet" data-testid="devis-type-projet" className={inputCls} value={form.type_projet} onChange={set("type_projet")}>
            <option value="" className="bg-[#111]">Sélectionner</option>
            <option value="Neuf" className="bg-[#111]">Construction neuve</option>
            <option value="Rénovation" className="bg-[#111]">Rénovation</option>
            <option value="Extension" className="bg-[#111]">Extension</option>
          </select>
        </div>
        <div>
          <label htmlFor="surface" className={labelCls}>Surface approximative</label>
          <input id="surface" data-testid="devis-surface" className={inputCls} value={form.surface} onChange={set("surface")} placeholder="ex. 60 m²" />
        </div>
        <div>
          <label htmlFor="revetement" className={labelCls}>Revêtement recherché</label>
          <select id="revetement" data-testid="devis-revetement" className={inputCls} value={form.revetement} onChange={set("revetement")}>
            <option value="" className="bg-[#111]">Sélectionner</option>
            <option value="Résine décorative" className="bg-[#111]">Résine décorative</option>
            <option value="Résine industrielle" className="bg-[#111]">Résine industrielle</option>
            <option value="Résine pailletée" className="bg-[#111]">Résine pailletée</option>
            <option value="Béton ciré" className="bg-[#111]">Béton ciré</option>
            <option value="Moquette de pierre" className="bg-[#111]">Moquette de pierre</option>
            <option value="Peinture bâtiment" className="bg-[#111]">Peinture bâtiment</option>
            <option value="Je ne sais pas encore" className="bg-[#111]">Je ne sais pas encore</option>
          </select>
        </div>
        <div>
          <label htmlFor="delai" className={labelCls}>Délai souhaité</label>
          <select id="delai" data-testid="devis-delai" className={inputCls} value={form.delai} onChange={set("delai")}>
            <option value="" className="bg-[#111]">Sélectionner</option>
            <option value="Dès que possible" className="bg-[#111]">Dès que possible</option>
            <option value="Sous 1 à 3 mois" className="bg-[#111]">Sous 1 à 3 mois</option>
            <option value="Sous 3 à 6 mois" className="bg-[#111]">Sous 3 à 6 mois</option>
            <option value="Je me renseigne" className="bg-[#111]">Je me renseigne pour l'instant</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="description" className={labelCls}>Décrivez votre projet</label>
        <textarea
          id="description"
          data-testid="devis-description"
          rows={5}
          className={`${inputCls} resize-none`}
          value={form.description}
          onChange={set("description")}
          placeholder="Nature des lieux, état du sol existant, ambiance recherchée, contraintes particulières…"
        />
      </div>

      <div>
        <label htmlFor="photos" className={labelCls}>Photos du projet (optionnel)</label>
        <label
          htmlFor="photos"
          data-testid="devis-photos-label"
          className="flex cursor-pointer items-center gap-4 border border-dashed border-white/20 px-5 py-6 text-sm text-[#8C8C8C] transition-colors hover:border-[#2FBF8F]/60 hover:text-[#F4F4F0]"
        >
          <Upload size={18} className="text-[#2FBF8F]" />
          {photos.length > 0
            ? `${photos.length} photo${photos.length > 1 ? "s" : ""} sélectionnée${photos.length > 1 ? "s" : ""}`
            : "Ajouter des photos de votre espace (5 max)"}
        </label>
        <input
          id="photos"
          data-testid="devis-photos"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => setPhotos(Array.from(e.target.files).slice(0, 5))}
        />
      </div>

      <AnimatePresence>
        {status === "error" && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="devis-error">
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      <button
        type="submit"
        data-testid="devis-submit"
        disabled={status === "loading"}
        className="group relative inline-flex w-full items-center justify-center overflow-hidden bg-[#047857] px-8 py-5 text-xs font-medium uppercase tracking-[0.22em] text-white transition-opacity disabled:opacity-60 sm:w-auto"
      >
        <span className="absolute inset-0 -translate-x-full bg-[#065F46] transition-transform duration-500 ease-out group-hover:translate-x-0" />
        <span className="relative flex items-center gap-3">
          {status === "loading" ? (
            <>Envoi en cours <Loader2 size={15} className="animate-spin" /></>
          ) : (
            <>Recevoir mon devis <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" /></>
          )}
        </span>
      </button>
      <p className="text-xs leading-relaxed text-[#8C8C8C]">
        Vos informations sont utilisées uniquement pour traiter votre demande. Vous ne savez pas quel revêtement choisir ? Décrivez-nous votre projet et Resineko vous orientera vers la solution la plus adaptée.
      </p>
    </form>
  );
};
